import React, { useEffect, useState } from 'react';
import {
    Box,
    Typography,
    Paper,
    TextField,
    Button,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    CircularProgress,
    Alert,
    Breadcrumbs,
    Link,
    Switch,
    FormControlLabel,
    InputAdornment,
    IconButton,
    Grid,
} from '@mui/material';
import { Save, ArrowBack, Visibility, VisibilityOff } from '@mui/icons-material';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { createPartner, updatePartner, getServiceCategories, getPartnerById } from '../../services/Api';

const CreateAndModifyPartner = () => {
    const { id } = useParams();
    const location = useLocation();
    const navigate = useNavigate();
    const isEdit = !!id;

    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [success, setSuccess] = useState(false);

    const [showPassword, setShowPassword] = useState(false);
    const [serviceCategories, setServiceCategories] = useState([]);
    const [loadingCategories, setLoadingCategories] = useState(false);
    const [mobileError, setMobileError] = useState('');
    const [passwordError, setPasswordError] = useState('');

    const [formData, setFormData] = useState({
        name: '',
        mobile: '',
        gender: 'male',
        password: '',
        partnerType: 'Grocery',
        categoryName: '',
        status: true,
        codAmount: '',
    });

    useEffect(() => {
        if (isEdit) {
            if (location.state?.partner) {
                const { partner } = location.state;
                setFormData({
                    name: partner.name || '',
                    mobile: partner.mobile || '',
                    gender: partner.gender || 'male',
                    password: '',
                    partnerType: partner.partnerType || 'Grocery',
                    categoryName: partner.categoryName || '',
                    status: partner.status !== undefined ? partner.status : (partner.isActive !== undefined ? partner.isActive : true),
                    codAmount: partner.codAmount !== undefined ? partner.codAmount : '',
                });
            } else {
                fetchPartnerDetails();
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isEdit, id, location.state]);

    const fetchPartnerDetails = async () => {
        setLoading(true);
        try {
            const response = await getPartnerById(id);
            if (response.success && response.data) {
                const partner = response.data;
                setFormData({
                    name: partner.name || '',
                    mobile: partner.mobile || '',
                    gender: partner.gender || 'male',
                    password: '',
                    partnerType: partner.partnerType || 'Grocery',
                    categoryName: partner.categoryName || '',
                    status: partner.status !== undefined ? partner.status : (partner.isActive !== undefined ? partner.isActive : true),
                    codAmount: partner.codAmount !== undefined ? partner.codAmount : '',
                });
            }
        } catch (err) {
            setError(err.response?.data?.message || err.message || 'Failed to fetch partner details');
            console.error(err);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (formData.partnerType === 'Services') {
            fetchServiceCategories();
        }
    }, [formData.partnerType]);

    const fetchServiceCategories = async () => {
        setLoadingCategories(true);
        try {
            const response = await getServiceCategories(1, 100);
            if (response.success || response.data) {
                setServiceCategories(response.data || []);
            }
        } catch (err) {
            console.error('Failed to fetch service categories:', err);
        } finally {
            setLoadingCategories(false);
        }
    };

    const handleInputChange = (e) => {
        const { name, value } = e.target;

        if (name === 'name') {
            if (!/^[a-zA-Z\s]*$/.test(value)) {
                return;
            }
        }

        if (name === 'mobile') {
            if (!/^\d*$/.test(value)) {
                setMobileError('only numeric values are allowed');
                return;
            }
            if (value.startsWith('0')) {
                setMobileError('Mobile number cannot start with zero');
                return;
            }
            if (value.length > 10) {
                setMobileError('Mobile number should be 10 digits');
            } else if (value.length > 0 && value.length < 10) {
                setMobileError('Mobile number must be 10 digits');
            } else {
                setMobileError('');
            }
        }
        if (name === 'password') {
            if (value.includes(' ')) {
                return;
            }
            if (value.length > 0) {
                const hasUpperCase = /[A-Z]/.test(value);
                const hasLowerCase = /[a-z]/.test(value);
                const hasNumbers = /\d/.test(value);
                const hasSpecialChars = /[^A-Za-z0-9]/.test(value);

                if (value.length < 8) {
                    setPasswordError('Password must be at least 8 characters');
                } else if (!hasUpperCase || !hasLowerCase || !hasNumbers || !hasSpecialChars) {
                    setPasswordError('Must contain uppercase, lowercase, number, and special character');
                } else {
                    setPasswordError('');
                }
            } else {
                setPasswordError('');
            }
        }
        setFormData((prev) => ({ ...prev, [name]: value }));
    };

    const handleSwitchChange = (e) => {
        setFormData((prev) => ({ ...prev, status: e.target.checked }));
    };

    const handleClickShowPassword = () => setShowPassword((show) => !show);
    const handleMouseDownPassword = (event) => {
        event.preventDefault();
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (formData.mobile.length !== 10) {
            setMobileError('Mobile number must be exactly 10 digits');
            return;
        }

        if (formData.mobile.startsWith('0')) {
            setMobileError('Mobile number cannot start with zero');
            return;
        }

        if (!isEdit && !formData.password) {
            setPasswordError('Password is required');
            return;
        }

        if (!isEdit && !formData.status) {
            setError('Partner status must be Active during registration');
            return;
        }

        if (formData.password) {
            const hasUpperCase = /[A-Z]/.test(formData.password);
            const hasLowerCase = /[a-z]/.test(formData.password);
            const hasNumbers = /\d/.test(formData.password);
            const hasSpecialChars = /[^A-Za-z0-9]/.test(formData.password);

            if (formData.password.length < 8) {
                setPasswordError('Password must be at least 8 characters');
                return;
            } else if (!hasUpperCase || !hasLowerCase || !hasNumbers || !hasSpecialChars) {
                setPasswordError('Must contain uppercase, lowercase, number, and special character');
                return;
            }
        }

        setLoading(true);
        setError(null);
        setSuccess(false);

        // Application/json payload as requested
        const payload = {
            name: formData.name,
            mobile: formData.mobile,
            gender: formData.gender,
            partnerType: formData.partnerType,
            status: formData.status,
            codAmount: formData.codAmount ? Number(formData.codAmount) : 0,
        };

        // Add categoryName only if Services is selected and a category is chosen
        if (formData.partnerType === 'Services' && formData.categoryName) {
            payload.categoryName = formData.categoryName;
        }

        if (formData.password) {
            payload.password = formData.password;
        }

        try {
            let response;
            if (isEdit) {
                response = await updatePartner(id, payload);
            } else {
                response = await createPartner(payload);
            }

            if (response.success) {
                setSuccess(true);
                setTimeout(() => navigate('/partners'), 1500);
            } else {
                const msg = response.message || 'Failed to save partner details';
                setError(msg);
                // Map server-side password error to the field
                if (msg.toLowerCase().includes('password')) {
                    setPasswordError(msg);
                }
            }
        } catch (err) {
            const msg = err.response?.data?.message || 'An error occurred while saving the partner';
            setError(msg);

            // Map server-side password error to the field
            if (msg.toLowerCase().includes('password')) {
                setPasswordError(msg);
            }

            console.error(err);
        } finally {
            setLoading(false);
        }
    };

    return (
        <Box>
            {/* Header Section */}
            <Box sx={{ mb: 4 }}>
                <Breadcrumbs aria-label="breadcrumb" sx={{ mb: 1 }}>
                    <Link
                        underline="hover"
                        color="inherit"
                        href="#"
                        onClick={(e) => {
                            e.preventDefault();
                            navigate('/partners');
                        }}
                        sx={{ display: 'flex', alignItems: 'center' }}
                    >
                        <ArrowBack sx={{ mr: 0.5, fontSize: 'inherit' }} />
                        Partners
                    </Link>
                    <Typography color="text.primary">{isEdit ? 'Edit Partner' : 'Register Partner'}</Typography>
                </Breadcrumbs>
                <Typography variant="h4" sx={{ fontWeight: 300 }}>
                    {isEdit ? 'Modify Delivery Partner' : 'Register New Partner'}
                </Typography>
            </Box>

            <Paper elevation={0} sx={{ p: 4, borderRadius: 2, border: '1px solid', borderColor: 'text.border' }}>
                {error && <Alert severity="error" sx={{ mb: 3 }}>{error}</Alert>}
                {success && <Alert severity="success" sx={{ mb: 3 }}>Partner details saved successfully!</Alert>}

                <form onSubmit={handleSubmit}>
                    <Grid container spacing={4}>
                        <Grid size={{ xs: 12, md: 6 }}>
                            <TextField
                                fullWidth
                                size='small'
                                label="Full Name"
                                name="name"
                                value={formData.name}
                                onChange={handleInputChange}
                                required
                                placeholder="E.g. John Doe"
                            />
                        </Grid>
                        <Grid size={{ xs: 12, md: 6 }}>
                            <TextField
                                fullWidth
                                size='small'
                                label="Mobile Number"
                                name="mobile"
                                value={formData.mobile}
                                onChange={handleInputChange}
                                required
                                error={!!mobileError}
                                helperText={mobileError}
                                placeholder="E.g. 9876543210"
                            />
                        </Grid>
                        <Grid size={{ xs: 12, md: 6 }}>
                            <FormControl fullWidth size='small' required>
                                <InputLabel id="gender-select-label">Gender</InputLabel>
                                <Select
                                    labelId="gender-select-label"
                                    name="gender"
                                    value={formData.gender}
                                    label="Gender"
                                    onChange={handleInputChange}
                                >
                                    <MenuItem value="male">Male</MenuItem>
                                    <MenuItem value="female">Female</MenuItem>
                                    <MenuItem value="other">Other</MenuItem>
                                </Select>
                            </FormControl>
                        </Grid>
                        <Grid size={{ xs: 12, md: 6 }}>
                            <FormControl fullWidth size='small' required>
                                <InputLabel id="partner-type-label">Delivery Type</InputLabel>
                                <Select
                                    labelId="partner-type-label"
                                    name="partnerType"
                                    value={formData.partnerType}
                                    label="Delivery Type"
                                    onChange={handleInputChange}
                                >
                                    <MenuItem value="Grocery">Grocery</MenuItem>
                                    <MenuItem value="Food">Food</MenuItem>
                                    <MenuItem value="Services">Services</MenuItem>
                                </Select>
                            </FormControl>
                        </Grid>
                        {formData.partnerType === 'Services' && (
                            <Grid size={{ xs: 12, md: 6 }}>
                                <FormControl fullWidth size='small'>
                                    <InputLabel>Service Category</InputLabel>
                                    <Select
                                        name="categoryName"
                                        value={formData.categoryName}
                                        onChange={handleInputChange}
                                        label="Service Category"
                                        disabled={loadingCategories}
                                    >
                                        {loadingCategories ? (
                                            <MenuItem disabled>Loading categories...</MenuItem>
                                        ) : serviceCategories.length > 0 ? (
                                            serviceCategories.map((category) => (
                                                <MenuItem key={category.id || category._id} value={category.name || category.categoryName}>
                                                    {category.name || category.categoryName}
                                                </MenuItem>
                                            ))
                                        ) : (
                                            <MenuItem disabled>No categories available</MenuItem>
                                        )}
                                    </Select>
                                </FormControl>
                            </Grid>
                        )}
                        <Grid size={{ xs: 12, md: 6 }}>
                            <TextField
                                fullWidth
                                size='small'
                                label={isEdit ? "New Password (Leave empty to keep current)" : "Password"}
                                name="password"
                                type={showPassword ? 'text' : 'password'}
                                value={formData.password}
                                onChange={handleInputChange}
                                required={!isEdit}
                                error={!!passwordError}
                                helperText={passwordError || "Password must be at least 8 characters with a mix of uppercase, lowercase, numbers, and special characters (@, $, etc.)"}
                                InputProps={{
                                    endAdornment: (
                                        <InputAdornment position="end">
                                            <IconButton
                                                aria-label="toggle password visibility"
                                                onClick={handleClickShowPassword}
                                                onMouseDown={handleMouseDownPassword}
                                                edge="end"
                                            >
                                                {showPassword ? <VisibilityOff /> : <Visibility />}
                                            </IconButton>
                                        </InputAdornment>
                                    )
                                }}
                            />
                        </Grid>
                        <Grid size={{ xs: 12, md: 6 }}>
                            <TextField
                                fullWidth
                                size='small'
                                label="COD Amount to Collect"
                                name="codAmount"
                                type="number"
                                value={formData.codAmount}
                                onChange={handleInputChange}
                                helperText="Note: Amount to be collected from the partner"
                                placeholder="E.g. 500"
                            />
                        </Grid>
                        <Grid size={{ xs: 12, md: 6 }}>
                            <Box sx={{ height: '100%', display: 'flex', alignItems: 'center', ml: 1 }}>
                                <FormControlLabel
                                    control={
                                        <Switch
                                            checked={formData.status}
                                            onChange={handleSwitchChange}
                                            color="primary"
                                        />
                                    }
                                    label={formData.status ? "Status: Active" : "Status: Inactive"}
                                />
                            </Box>
                        </Grid>
                    </Grid>

                    <Box sx={{ mt: 5, display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
                        <Button
                            variant="outlined"
                            onClick={() => navigate('/partners')}
                            disabled={loading}
                            sx={{ px: 4 }}
                        >
                            Cancel
                        </Button>
                        <Button
                            type="submit"
                            variant="contained"
                            startIcon={loading ? <CircularProgress size={20} color="inherit" /> : <Save />}
                            disabled={loading}
                            sx={{ px: 6, backgroundColor: 'primary.main' }}
                        >
                            {isEdit ? 'Update Partner' : 'Register Partner'}
                        </Button>
                    </Box>
                </form>
            </Paper>
        </Box>
    );
};

export default CreateAndModifyPartner;
